// src/pages/DriverHomeScreen.js (REPLACE ENTIRE FILE)
import React, { useEffect, useMemo, useState } from "react";
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  Pressable,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
  StatusBar,
} from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import SecretCodeModal from "../components/SecretCodeModal";
import { useTranslation } from "../i18n";

const heroDriver = require("../../assets/driver.png");
const cityLogo = require("../../assets/urban.png");
const tickCircle = require("../../assets/tickcircle.png");
const xTickCircle = require("../../assets/xtickcircle.png");
const shieldLock = require("../../assets/shieldd.png");

const GREEN = "#2F7D3A";
const RED = "#DC2626";
const DARK_GREEN = "#0F3C2F";
const MUTED = "rgba(107,114,128,0.95)";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const isNumericId = (v) => /^\d+$/.test(String(v || "").trim());

// ✅ web: extract /driver/:id
const getDriverIdFromPathname = () => {
  if (Platform.OS !== "web") return null;
  try {
    const segs = (window.location?.pathname || "").split("/").filter(Boolean);
    const i = segs.findIndex((s) => String(s).toLowerCase() === "driver");
    const id = i !== -1 ? segs[i + 1] : null;
    return id ? String(id).trim() : null;
  } catch {
    return null;
  }
};

// ✅ treat as missing if empty / dash / "null"
const isMissing = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return true;
  if (s === "—" || s === "-") return true;
  if (s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return true;
  return false;
};

// ✅ safe helper for photo URL (new API)
const getDriverPhotoUrl = (payload) => {
  const url = payload?.documents_by_type?.photo?.public_url;
  const s = String(url ?? "").trim();
  if (!s) return null;
  if (!/^https?:\/\//i.test(s)) return null;
  return s;
};

export default function DriverHomeScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { height, width } = useWindowDimensions();
  const { lang, setLang, t } = useTranslation();

  const statusBarH = Platform.OS === "android" ? StatusBar.currentHeight || 0 : 0;
  const usableH = height - statusBarH;

  const scale = clamp(usableH / 820, 0.86, 1.08);

  const HERO_H = clamp(usableH * 0.22, 120, 190);
  const LOGO_H = clamp(usableH * 0.18, 95, 175);
  const TICK = clamp(48 * scale, 40, 56);
  const STATUS_FONT = clamp(30 * scale, 22, 32);
  const CARD_TITLE = clamp(16 * scale, 14, 16);
  const CARD_SUB = clamp(15 * scale, 13, 15);
  const VALUE_FONT = clamp(18 * scale, 16, 20);
  const BTN_FONT = clamp(22 * scale, 18, 22);
  const BTN_H = clamp(48 * scale, 42, 52);

  const isNarrow = width < 360;
  const HERO_UP = isNarrow ? -18 : -14;

  // ✅ NEW: Driver deep link param name is "id" from App.js
  const idFromParams =
    route?.params?.id != null
      ? String(route.params.id).trim()
      : route?.params?.ref != null
      ? String(route.params.ref).trim()
      : null;

  const idFromPath = getDriverIdFromPathname();

  const personId = useMemo(() => {
    const raw = idFromParams || idFromPath || null;
    if (!raw) return null;
    return isNumericId(raw) ? raw : null;
  }, [idFromParams, idFromPath]);

  const [langOpen, setLangOpen] = useState(false);
  const [codeModalOpen, setCodeModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [hasError, setHasError] = useState(false);

  // ✅ NEW: remote photo URL (optional)
  const [photoUrl, setPhotoUrl] = useState(null);

  // modes: UNKNOWN | CHECKING | VERIFIED | NOT_VERIFIED
  const [statusMode, setStatusMode] = useState("UNKNOWN");

  useEffect(() => {
    if (!personId) {
      setData(null);
      setPhotoUrl(null);
      setLoading(false);
      setHasError(false);
      setStatusMode("UNKNOWN");
      return;
    }

    setLoading(true);
    setHasError(false);
    setStatusMode("CHECKING");

  const url = `https://api.taxi-yaounde.com/api/drivers/person/${encodeURIComponent(
  personId
)}`;

    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        if (json?.success && json?.data) {
          setData(json.data);
          setHasError(false);

          // ✅ YOUR RULE: if driver exists for this person_id => VERIFIED
          const ok = json?.data?.person_id != null;
          setStatusMode(ok ? "VERIFIED" : "NOT_VERIFIED");

          // ✅ NEW: set photo if exists, else keep fallback
          const urlFromApi = getDriverPhotoUrl(json.data);

          // (optional) cache-bust on web so updates show immediately
          const finalUrl =
            urlFromApi && Platform.OS === "web"
              ? `${urlFromApi}${urlFromApi.includes("?") ? "&" : "?"}v=${Date.now()}`
              : urlFromApi;

          setPhotoUrl(finalUrl || null);
        } else {
          setData(null);
          setPhotoUrl(null);
          setHasError(true);
          setStatusMode("NOT_VERIFIED");
        }
        setLoading(false);
      })
      .catch(() => {
        setData(null);
        setPhotoUrl(null);
        setHasError(true);
        setStatusMode("NOT_VERIFIED");
        setLoading(false);
      });
  }, [personId]);

  const FLAGS = { fr: "🇫🇷", en: "🇬🇧" };

  const statusUI = useMemo(() => {
    if (statusMode === "VERIFIED") {
      return { icon: tickCircle, color: GREEN, text: t("home.verified") };
    }
    if (statusMode === "NOT_VERIFIED") {
      return { icon: xTickCircle, color: RED, text: t("home.notVerified") };
    }
    return { icon: tickCircle, color: MUTED, text: "—" };
  }, [statusMode, t]);

  // ✅ Map API data to UI
  const nameToShow = data?.name ? String(data.name) : "—";
  const licenceToShow = data?.license_number ? String(data.license_number) : "—";
  const cniToShow = !isMissing(data?.cin_number) ? String(data.cin_number) : "—";

  // ✅ hero image: remote if available else fallback local asset
  const heroSource = photoUrl ? { uri: photoUrl } : heroDriver;

  const handleConfirmCode = () => {
    setCodeModalOpen(false);

    navigation.navigate("driver_official_control_screen", {
      id: personId,
    });
  };

  return (
    <SafeAreaView style={styles.safe}>
      <View style={styles.screen}>
        {/* ✅ Floating Language Selector */}
        <View style={styles.langRow}>
          <View style={styles.langDropdownWrap}>
            <Pressable onPress={() => setLangOpen((v) => !v)} style={styles.langDropdownBtn}>
              <Text style={styles.langDropdownText}>
                {FLAGS[lang]} {lang === "fr" ? "FR" : "EN"}
              </Text>
              <Text style={styles.langDropdownCaret}>{langOpen ? "▲" : "▼"}</Text>
            </Pressable>

            {langOpen && (
              <View style={styles.langDropdownMenu}>
                <Pressable
                  onPress={() => {
                    setLang("fr");
                    setLangOpen(false);
                  }}
                  style={styles.langDropdownItem}
                >
                  <Text style={styles.langDropdownItemText}>
                    {FLAGS.fr} {t("lang.fr")}
                  </Text>
                </Pressable>

                <Pressable
                  onPress={() => {
                    setLang("en");
                    setLangOpen(false);
                  }}
                  style={styles.langDropdownItem}
                >
                  <Text style={styles.langDropdownItemText}>
                    {FLAGS.en} {t("lang.en")}
                  </Text>
                </Pressable>
              </View>
            )}
          </View>
        </View>

        {/* Middle content (no scroll) */}
        <View style={[styles.mid, { paddingTop: 38 }]}>
          <Image
            source={heroSource}
            style={[styles.heroImg, { height: HERO_H, marginTop: HERO_UP }]}
            resizeMode="contain"
            onError={() => {
              // ✅ if remote image fails to load, fallback to local
              setPhotoUrl(null);
            }}
          />

          <View style={styles.statusRow}>
            <Image source={statusUI.icon} style={{ width: TICK, height: TICK }} resizeMode="contain" />
            <Text
              style={[styles.statusText, { fontSize: STATUS_FONT, color: statusUI.color }]}
              numberOfLines={1}
            >
              {statusUI.text}
            </Text>
          </View>

          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Text style={[styles.cardHeaderTitle, { fontSize: CARD_TITLE }]}>
                {t("driver.profileTitle")}{" "}
                <Text style={[styles.cardHeaderSub, { fontSize: CARD_SUB }]}>
                  ({t("driver.profileSub")})
                </Text>
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>{t("driver.name")}</Text>
              <Text style={[styles.value, { fontSize: VALUE_FONT }]} numberOfLines={1}>
                {nameToShow}
              </Text>
            </View>
            <View style={styles.sep} />

            <View style={styles.row}>
              <Text style={styles.label}>{t("driver.permit")}</Text>
              <Text style={[styles.value, { fontSize: VALUE_FONT }]} numberOfLines={1}>
                {licenceToShow}
              </Text>
            </View>
            <View style={styles.sep} />

            <View style={styles.row}>
              <Text style={styles.label}>{t("driver.cni")}</Text>
              <Text style={[styles.value, { fontSize: VALUE_FONT }]} numberOfLines={1}>
                {cniToShow}
              </Text>
            </View>

            <TouchableOpacity
              activeOpacity={0.92}
              style={[styles.officialBtn, { height: BTN_H }]}
              onPress={() => {
                setLangOpen(false);
                setCodeModalOpen(true);
              }}
            >
              <Image source={shieldLock} style={styles.btnIcon} resizeMode="contain" />
              <Text style={[styles.btnText, { fontSize: BTN_FONT }]}>
                {t("home.officialControl")}
              </Text>
            </TouchableOpacity>
          </View>

          {loading && (
            <View style={{ marginTop: 6 }}>
              <ActivityIndicator size="small" color={MUTED} />
            </View>
          )}

          {hasError ? <Text style={styles.errorText}>{t("errors.backendNotReady")}</Text> : null}
        </View>

        {/* Bottom logo */}
        <View style={styles.bottom}>
          <Image source={cityLogo} style={[styles.logo, { height: LOGO_H }]} resizeMode="contain" />
        </View>

        {/* Driver Secret Code */}
        <SecretCodeModal
          visible={codeModalOpen}
          onClose={() => setCodeModalOpen(false)}
          onConfirm={handleConfirmCode}
          expectedCode={"9520"}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: "#F5F5F5" },

  screen: {
    flex: 1,
    paddingTop: 14,
    paddingHorizontal: 18,
    alignItems: "center",
  },

  langRow: {
    position: "absolute",
    top: 14,
    right: 18,
    zIndex: 9999,
  },
  langDropdownWrap: { position: "relative" },
  langDropdownBtn: {
    height: 34,
    minWidth: 92,
    paddingHorizontal: 12,
    borderRadius: 18,
    backgroundColor: DARK_GREEN,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  langDropdownText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "800",
    letterSpacing: 0.5,
    lineHeight: 18,
  },
  langDropdownCaret: { color: "rgba(255,255,255,0.9)", fontSize: 12, fontWeight: "900" },
  langDropdownMenu: {
    position: "absolute",
    top: 40,
    right: 0,
    width: 170,
    borderRadius: 14,
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.18,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 10 },
    elevation: 8,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    zIndex: 9999,
  },
  langDropdownItem: { paddingVertical: 12, paddingHorizontal: 14 },
  langDropdownItemText: { fontSize: 14, fontWeight: "700", color: "#111827", lineHeight: 18 },

  mid: {
    flex: 1,
    width: "100%",
    alignItems: "center",
    justifyContent: "flex-start",
  },

  heroImg: { width: "100%" },

  statusRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 0,
    gap: 12,
    minHeight: 46,
  },
  statusText: { fontWeight: "700" },

  card: {
    width: "100%",
    marginTop: 4,
    borderRadius: 22,
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 12 },
    elevation: 4,
    paddingBottom: 10,
  },

  cardHeader: { backgroundColor: "#DCEEDD", paddingVertical: 10, paddingHorizontal: 14 },
  cardHeaderTitle: { fontWeight: "900", color: "#1F6F3D" },
  cardHeaderSub: { fontWeight: "900", color: "#557B64" },

  row: { paddingHorizontal: 14, paddingVertical: 9 },
  label: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.55)" },
  value: { marginTop: 4, fontWeight: "900", color: "#111827" },
  sep: { height: 1, backgroundColor: "rgba(15,23,42,0.08)", marginHorizontal: 14 },

  officialBtn: {
    alignSelf: "center",
    width: "82%",
    backgroundColor: DARK_GREEN,
    borderRadius: 18,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: 54,
    paddingRight: 16,
    marginTop: 10,
  },
  btnIcon: { position: "absolute", left: 16, width: 30, height: 30 },
  btnText: { color: "#FFFFFF", fontWeight: "900" },

  errorText: {
    marginTop: 8,
    fontSize: 12,
    color: "rgba(220,38,38,0.92)",
    fontWeight: "800",
    textAlign: "center",
  },

  bottom: {
    width: "100%",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingBottom: 10,
    marginTop: 2,
  },
  logo: { width: 265 },
});
